/**
 * Gestor de cámara SERIALIZADO — dueño único del `MediaStream` de la app.
 *
 * Vive fuera del componente a propósito: así sobrevive al doble montaje del
 * StrictMode y hay UN solo stream para toda la aplicación (solo puede haber un
 * modal a la vez, porque el sistema de modales es de una única ranura).
 *
 * ⚠️ ESTA ES LA ZONA FRÁGIL. Es el resultado de perseguir el `NotReadableError`
 * de las tablets Samsung (front #126, 2026-07-31): la cámara física de Android
 * admite UNA sola apertura a la vez y NO libera el sensor al instante tras
 * `track.stop()`. Dos `getUserMedia` solapados → "Could not start video source"
 * y el objetivo "clicando" varias veces.
 *
 * Se extrajo de `index.tsx` **sin cambiar una línea de su lógica** al añadir
 * zoom / linterna / enfoque. Ninguna de esas capacidades abre cámaras: todas
 * son `applyConstraints` sobre un track ya vivo. Si vas a tocar este fichero,
 * lee antes `tests/unit/camera/camera-manager.spec.ts`, que lo congela.
 */

/**
 * Poner a `true` para reactivar el logging detallado de la cámara al depurar en
 * tablet (`chrome://inspect`). En `false` (por defecto) main queda sin ruido.
 */
const CAMERA_DEBUG = false;

export const logCam = (label: string, data?: unknown): void => {
  if (CAMERA_DEBUG) console.info(`[Camera] ${label}`, data ?? '');
};

/** Extrae los campos útiles de un error de MediaDevices para loguear/mostrar. */
export const errInfo = (err: unknown): Record<string, unknown> => {
  const e = err as {
    name?: string;
    message?: string;
    constraint?: string;
  };
  return { name: e?.name, message: e?.message, constraint: e?.constraint };
};

/** Resumen legible de un MediaDeviceInfo para el log (deviceId recortado). */
export const deviceSummary = (
  d: MediaDeviceInfo
): Record<string, unknown> => ({
  label: d.label || '(sin label)',
  deviceId: d.deviceId ? `${d.deviceId.slice(0, 8)}…` : '(vacío)',
  kind: d.kind,
});

/** Para todos los tracks de un stream (libera la cámara). Null-safe. */
export const stopStream = (stream: MediaStream | null | undefined): void => {
  stream?.getTracks().forEach((t) => t.stop());
};

/** Espera ms — respiro para que el sensor Samsung libere entre aperturas. */
const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Tiempo que tarda la cámara en soltar el sensor tras `track.stop()` en tablets
 * Android (confirmado en discuss-webrtc / issues.chromium: puede tardar). Sin
 * este respiro entre un getUserMedia y el siguiente sobre la misma cámara, el 2º
 * falla con NotReadableError.
 */
export const CAMERA_RELEASE_MS = 300;

let activeCameraStream: MediaStream | null = null;
let cameraChain: Promise<unknown> = Promise.resolve();

/**
 * Nº de instancias del modal montadas. Sirve para NO soltar la cámara en el
 * cleanup sintético de StrictMode (montar→limpiar→montar): en el cleanup se
 * programa un microtask y solo se suelta si tras él seguimos desmontados.
 */
let cameraModalMounted = 0;

export const retainCameraModal = (): void => {
  cameraModalMounted += 1;
};

export const releaseCameraModal = (): void => {
  cameraModalMounted -= 1;
};

export const noCameraModalMounted = (): boolean => cameraModalMounted === 0;

export const getActiveCameraStream = (): MediaStream | null =>
  activeCameraStream;

export const releaseActiveCamera = (): void => {
  stopStream(activeCameraStream);
  activeCameraStream = null;
};

export const activeCameraDeviceId = (): string | undefined =>
  activeCameraStream?.getVideoTracks()[0]?.getSettings().deviceId;

/** deviceId solicitado en unas constraints (para deduplicar aperturas). */
export const requestedDeviceId = (
  c: MediaStreamConstraints
): string | undefined => {
  const v = c.video;
  if (!v || typeof v !== 'object' || !('deviceId' in v)) return undefined;
  const d = (v as MediaTrackConstraints).deviceId;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d[0];
  if (d && typeof d === 'object') {
    const first = (x: string | string[] | undefined): string | undefined =>
      Array.isArray(x) ? x[0] : x;
    return first(d.ideal) ?? first(d.exact);
  }
  return undefined;
};

export const acquireCamera = (
  constraints: MediaStreamConstraints
): Promise<MediaStream> => {
  const run = async (): Promise<MediaStream> => {
    // DEDUP (fix "5 clics" 2026-07-31): si YA tenemos abierta esta cámara (o se
    // pide una genérica y hay alguna viva), REUSAR el stream — evita reabrir la
    // misma cámara N veces por el cascade de efectos (StrictMode duplica
    // getCameras+initCamera, el listener de permisos re-lanza getCameras, etc.).
    const wantId = requestedDeviceId(constraints);
    if (
      activeCameraStream?.active &&
      (!wantId || activeCameraDeviceId() === wantId)
    ) {
      logCam('acquireCamera DEDUP → reuso stream vivo (sin reabrir)', {
        wantId,
      });
      return activeCameraStream;
    }
    const hadActive = !!activeCameraStream;
    releaseActiveCamera();
    if (hadActive) await sleep(CAMERA_RELEASE_MS);
    let lastErr: unknown;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        activeCameraStream = stream;
        return stream;
      } catch (err) {
        lastErr = err;
        if (
          (err as { name?: string })?.name !== 'NotReadableError' ||
          attempt === 3
        ) {
          throw err;
        }
        logCam(
          `getUserMedia NotReadableError → reintento ${attempt}/3`,
          errInfo(err)
        );
        await sleep(CAMERA_RELEASE_MS * (attempt + 1));
      }
    }
    throw lastErr;
  };
  // Cola: el siguiente acquire espera al anterior. La cadena NO se rompe si uno
  // falla (el siguiente debe poder correr igual).
  const result = cameraChain.then(run, run);
  cameraChain = result.then(
    () => undefined,
    () => undefined
  );
  return result;
};
